/**
 * ScanDiagnosticsPanel — compact operator trust diagnostics.
 *
 * Shows a single support-state status line plus key diagnostic facts
 * in a compact, operator-friendly format. Two variants:
 *   - compact: status line + top 3 lines (for dashboard)
 *   - full: all lines + footnote (for scan page)
 */

import type { DiagnosticSummary } from "../lib/diagnostics.js";

interface Props {
  summary: DiagnosticSummary;
  variant: "compact" | "full";
}

function toneClass(tone: string): string {
  switch (tone) {
    case "success":
      return "text-success";
    case "warning":
      return "text-warning";
    case "danger":
      return "text-danger";
    default:
      return "text-muted";
  }
}

function toneAlertClass(tone: string): string {
  switch (tone) {
    case "success":
      return "alert--info";
    case "warning":
      return "alert--warning";
    case "danger":
      return "alert--error";
    default:
      return "alert--info";
  }
}

export function ScanDiagnosticsPanel({ summary, variant }: Props) {
  const { supportState, lines, footnote } = summary;
  const displayLines = variant === "compact" ? lines.slice(0, 4) : lines;

  if (lines.length === 0 && supportState.state === "ready") {
    return null;
  }

  return (
    <div
      className={`alert ${toneAlertClass(supportState.tone)}`}
      role="status"
      aria-label="Scan diagnostics"
    >
      <div
        className="fw-600"
        style={{
          marginBottom: displayLines.length > 0 ? "8px" : 0,
        }}
      >
        <span className={toneClass(supportState.tone)}>
          {supportState.label}
        </span>
      </div>

      {displayLines.length > 0 && (
        <dl
          className="diagnostics-dl"
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "2px 12px",
            margin: 0,
            fontSize: "0.85rem",
          }}
        >
          {displayLines.map((line, i) => (
            <div key={i} style={{ display: "contents" }}>
              <dt
                className="text-muted fw-500"
                style={{ whiteSpace: "nowrap" }}
              >
                {line.label}
              </dt>
              <dd className={toneClass(line.tone)} style={{ margin: 0 }}>
                {line.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {variant === "full" && footnote && (
        <p
          className="text-muted text-sm mt-2"
          style={{ marginBottom: 0, fontStyle: "italic" }}
        >
          {footnote}
        </p>
      )}
    </div>
  );
}
